/**
 * Daily bars against a STEP line for the target — targets change at check-ins,
 * so a flat reference line would misstate every day before the last change.
 * Days that don't count toward the estimate (incomplete) are drawn hollow and
 * say so in the tooltip.
 */

import { useRef, useState } from 'react'

import { shortDate } from '../../lib/days'

export type Bar = { day: string; value: number; muted?: boolean; target?: number | null }

const W = 320
const PAD = { l: 38, r: 8, t: 8, b: 22 }

export function BarChart({ bars, height = 170, format, color = 'var(--accent)', label }: {
  bars: Bar[]
  height?: number
  format: (v: number) => string
  color?: string
  label: string
}) {
  const ref = useRef<SVGSVGElement>(null)
  const [hover, setHover] = useState<number | null>(null)
  if (bars.length === 0) return <p className="muted">Nothing logged in this range.</p>

  const hi = Math.max(1, ...bars.map((b) => Math.max(b.value, b.target ?? 0))) * 1.08
  const n = bars.length
  const slot = (W - PAD.l - PAD.r) / n
  // Capped, or one logged day becomes a slab the width of the chart.
  const bw = Math.min(22, Math.max(1.5, slot - (n > 60 ? 1 : 2)))
  const inset = (slot - bw) / 2
  const y = (v: number) => PAD.t + (1 - v / hi) * (height - PAD.t - PAD.b)
  const x = (i: number) => PAD.l + i * slot
  const ticks = [hi * 0.25, hi * 0.5, hi * 0.75]

  const step: string[] = []
  bars.forEach((b, i) => {
    if (b.target === null || b.target === undefined) return
    step.push(`${x(i)},${y(b.target)}`, `${x(i) + slot},${y(b.target)}`)
  })

  const onMove = (e: React.PointerEvent) => {
    const r = ref.current?.getBoundingClientRect()
    if (!r) return
    const px = ((e.clientX - r.left) / r.width) * W
    setHover(Math.max(0, Math.min(n - 1, Math.floor((px - PAD.l) / slot))))
  }
  const hb = hover !== null ? bars[hover] : null

  return (
    <div style={{ position: 'relative' }}>
      <svg ref={ref} viewBox={`0 0 ${W} ${height}`} width="100%" role="img" aria-label={label}
        onPointerMove={onMove} onPointerDown={onMove} onPointerLeave={() => setHover(null)}
        style={{ touchAction: 'pan-y', display: 'block' }}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} stroke="var(--border)" />
            <text x={PAD.l - 5} y={y(t) + 3.5} textAnchor="end" fontSize="9.5" fill="var(--muted)">{format(t)}</text>
          </g>
        ))}
        {bars.map((b, i) => {
          const top = y(b.value), h = Math.max(0, height - PAD.b - top)
          const r = Math.min(4, bw / 2, h)
          const d = `M${x(i) + inset},${height - PAD.b} v${-(h - r)} q0,${-r} ${r},${-r} h${bw - 2 * r} q${r},0 ${r},${r} v${h - r} z`
          return h > 0 ? (
            <path key={b.day} d={d} fill={b.muted ? 'none' : color} stroke={b.muted ? color : 'none'}
              strokeWidth={b.muted ? 1 : 0} opacity={hover === null || hover === i ? 1 : 0.55} />
          ) : null
        })}
        {step.length > 0 && (
          <polyline points={step.join(' ')} fill="none" stroke="var(--text)" strokeWidth="1.5" strokeDasharray="4 3" />
        )}
        <text x={PAD.l} y={height - 5} fontSize="9.5" fill="var(--muted)">{shortDate(bars[0].day)}</text>
        <text x={W - PAD.r} y={height - 5} fontSize="9.5" fill="var(--muted)" textAnchor="end">{shortDate(bars[n - 1].day)}</text>
      </svg>
      {hb && (
        <div className="chart-tip" style={{ left: `${Math.min(68, Math.max(2, ((x(hover!) / W) * 100) - 12))}%`, top: 0 }}>
          <div style={{ fontWeight: 600 }}>{shortDate(hb.day)}</div>
          <div>{format(hb.value)}{hb.target ? ` of ${format(hb.target)}` : ''}</div>
          {hb.muted && <div className="muted small">Not counted (incomplete)</div>}
        </div>
      )}
    </div>
  )
}
